// src/context/AuthContext.jsx

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

import { auth } from "../utils/firebase";

/* =====================================================
   ADMIN CONFIG — SINGLE SOURCE OF TRUTH
   ===================================================== */

const ADMIN_EMAILS = new Set([
  "hariharanmadhav@gmail.com",
  "mugiwaraaluffy007@gmail.com",
  "lalithaganesan449@gmail.com",
].map((e) => e.toLowerCase()));

/* =====================================================
   CONTEXT DEFINITION
   ===================================================== */

export const AuthContext = createContext(null);

/**
 * Safe hook wrapper
 * Prevents usage outside provider
 */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider />");
  }
  return ctx;
};

/* =====================================================
   PROVIDER
   ===================================================== */

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);

  /**
   * authReady = Firebase has fully resolved auth state
   * 🔥 IMPORTANT: Use this before running Firestore queries
   */
  const [authReady, setAuthReady] = useState(false);

  /**
   * internal flag to prevent state updates after unmount
   */
  const [mounted, setMounted] = useState(true);

  /* ===================================================
     AUTH STATE LISTENER (SINGLE, GLOBAL)
     =================================================== */

  useEffect(() => {
    setMounted(true);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!mounted) return;

      setCurrentUser(user ?? null);
      setAuthReady(true);
    });

    return () => {
      setMounted(false);
      unsubscribe();
    };
  }, [mounted]);

  /* ===================================================
     ADMIN CHECK (EMAIL-BASED — UI LEVEL)
     =================================================== */

  const isAdmin = useMemo(() => {
    if (!currentUser?.email) return false;
    return ADMIN_EMAILS.has(currentUser.email.toLowerCase());
  }, [currentUser?.email]);

  /* ===================================================
     GOOGLE AUTH (POPUP)
     =================================================== */

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();

    // Always show account chooser
    provider.setCustomParameters({
      prompt: "select_account",
    });

    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Google login failed:", error);
      throw error;
    }
  }, []);

  /* ===================================================
     LOGOUT
     =================================================== */

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      if (mounted) {
        setCurrentUser(null);
      }
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  }, [mounted]);

  /* ===================================================
     CONTEXT VALUE (STABLE)
     =================================================== */

  const contextValue = useMemo(
    () => ({
      // auth state
      currentUser,
      authReady,

      // roles
      isAdmin,

      // actions
      loginWithGoogle,
      logout,
    }),
    [currentUser, authReady, isAdmin, loginWithGoogle, logout]
  );

  /* ===================================================
     PROVIDER RENDER
     =================================================== */

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
